import MalKort from '../components/MalKort';
import React, { useState, useEffect } from 'react';
import { Clock, SubNav, Metric, KpiRow, DagligForsaljning, DagligSnabb, YoYUtforskare, VisaFlerRad } from '../components/SharedComponents';
import { tkr, fmtMonth } from '../utils';
import { getTopProducts } from '../qlik';

const STUG_COLOR = '#3B3B3B';
const PERIOD_LABELS = [
  { key: 'today',       label: 'Idag'      },
  { key: 'yesterday',   label: 'Igår'      },
  { key: 'week',        label: 'Vecka'     },
  { key: 'month',       label: 'Månad'     },
  { key: 'threemonths', label: '3 månader' },
];

const btnStyle = (active) => ({
  background: active ? STUG_COLOR : 'transparent',
  color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20,
  padding: '3px 10px', fontSize: 11, cursor: 'pointer',
  fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
});

// Återanvändbar topp-lista (Största kunder / Populäraste tjänster)
function StugTopList({ products, loading, dataKey, title, note }) {
  const [period, setPeriod] = useState('threemonths'); // säsongsbetonat → default 3 mån
  const [sortBy, setSortBy] = useState('intakt');       // 'intakt' | 'antal'
  const [visaAlla, setVisaAlla] = useState(false);

  const raw    = products?.stugservice?.[dataKey]?.[period] || [];
  const full   = [...raw].sort((a, b) => b[sortBy] - a[sortBy]);
  const items  = visaAlla ? full : full.slice(0, 7);
  const maxVal = Math.max(...items.map(i => i[sortBy] || 0), 1);

  const valLabel = (item) =>
    sortBy === 'antal' ? `${item.antal} st` : `${Math.round(item.intakt).toLocaleString('sv-SE')} kr`;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>{title}</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {PERIOD_LABELS.map(({ key, label }) => (
            <button key={key} onClick={() => setPeriod(key)} style={btnStyle(period === key)}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        <div style={{ flex: 1 }} />
        <button onClick={() => setSortBy('intakt')} style={btnStyle(sortBy === 'intakt')}>Intäkt</button>
        <button onClick={() => setSortBy('antal')}  style={btnStyle(sortBy === 'antal')}>Antal</button>
      </div>
      {loading && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar...</div>}
      {!loading && items.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen data för perioden</div>
      )}
      {!loading && items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
          <div style={{ width: 14, fontSize: 10, color: '#bbb', textAlign: 'right', fontFamily: 'system-ui,sans-serif', flexShrink: 0 }}>{i + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontSize: 11, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', flexShrink: 0, marginLeft: 8 }}>{valLabel(item)}</span>
            </div>
            <div style={{ height: 3, background: 'var(--panel2)', borderRadius: 2 }}>
              <div style={{ height: '100%', width: `${Math.round(item[sortBy] / maxVal * 100)}%`, background: STUG_COLOR, borderRadius: 2 }} />
            </div>
          </div>
        </div>
      ))}
      {!loading && <VisaFlerRad total={full.length} expanded={visaAlla} onToggle={() => setVisaAlla(v => !v)} />}
      <div className="note" style={{ marginTop: 10 }}>{note}</div>
    </div>
  );
}

export default function StugserviceBlock({ data }) {
  const [sub, setSub] = useState('daglig');
  const rows  = data?.monthly || [];
  const daily = data?.daily   || [];
  const ytd   = data?.ytd     || {};

  const [products, setProducts] = useState(null);
  const [loading,  setLoading]  = useState(true);
  useEffect(() => {
    getTopProducts()
      .then(p  => setProducts(p))
      .catch(e => console.error('StugserviceProducts error:', e))
      .finally(() => setLoading(false));
  }, []);

  const TABS = [
    { id: 'daglig', label: 'Daglig översikt' },
    { id: 'period', label: 'Period & trend'  },
  ];

  // Senaste månad med faktisk intäkt
  const activeRow = rows.find(r => (r[5] || 0) > 0) || rows[0];
  const r0Rev   = activeRow?.[5]  || 0;
  const r0TB1   = activeRow?.[41] || 0;
  const r0Tim   = activeRow?.[21] || 0;
  const r0TG    = r0Rev > 0 ? Math.round(r0TB1 / r0Rev * 100) : 0;
  const r0OmsH  = r0Tim > 0 ? Math.round(r0Rev / r0Tim)       : 0;
  const r0Label = activeRow?.[0] ? fmtMonth(activeRow[0])      : '';

  // Toppmånad (högst intäkt i datasetet)
  const bestRow = rows.reduce((best, r) => ((r[5] || 0) > (best?.[5] || 0) ? r : best), null);
  const bestRev   = bestRow?.[5] || 0;
  const bestLabel = bestRow?.[0] ? fmtMonth(bestRow[0]) : '';

  // YTD
  const ytdRev  = ytd.stugservice    || 0;
  const ytdTB1  = ytd.tb1Stugservice || 0;
  const ytdTim  = ytd.timStug        || 0;
  const ytdTG   = ytdRev > 0 ? Math.round(ytdTB1 / ytdRev * 100) : 0;
  const ytdOmsH = ytdTim > 0 ? Math.round(ytdRev / ytdTim)       : 0;
  const ytdLon  = Math.abs(ytd.lonStugservice || 0);
  const ytdPersPct = ytdRev > 0 && ytdLon > 0 ? Math.round(ytdLon / ytdRev * 100) : null;

  return (
    <div className="screen">
      <div className="header header-stugservice">
        <div>
          <div className="header-title">Stugservice</div>
          <div className="header-name">Städning & besiktning av privata stugor</div>
        </div>
        <Clock />
      </div>
      <SubNav tabs={TABS} active={sub} onChange={setSub} />
      {sub === 'daglig' && <MalKort enheter={['Stugservice']} />}

      {/* ── DAGLIG ÖVERSIKT ── */}
      {sub === 'daglig' && (
        <>
          <DagligSnabb daily={daily} enhet="stugservice" />
          <div className="grid-3">
            <Metric
              label={r0Label ? `Intäkt ${r0Label}` : 'Intäkt senaste månaden'}
              value={r0Rev > 0 ? tkr(r0Rev) : '–'}
              sub={r0TB1 !== 0 ? `TB1 ${tkr(r0TB1)} · TG ${r0TG}%` : ''}
              trend={r0TB1 > 0 ? 'up' : (r0TB1 < 0 ? 'down' : '')}
            />
            <Metric
              label="OMS/H"
              value={r0OmsH > 0 ? `${r0OmsH.toLocaleString('sv-SE')} kr/h` : '–'}
              sub={r0Tim > 0 ? `${Math.round(r0Tim)} tim` : ''}
            />
            <Metric
              label="Toppmånad"
              value={bestRev > 0 ? `${bestLabel} ${tkr(bestRev)}` : '–'}
              sub="Högsta månadsintäkt"
              trend="up"
            />
          </div>
          <div className="grid-2">
            <StugTopList products={products} loading={loading} dataKey="kunder"
              title="Största kunder" note="Källa: Qlik · Resultatenhet Stugservice (Kundnamn)" />
            <StugTopList products={products} loading={loading} dataKey="tjanster"
              title="Populäraste tjänster" note="Källa: Qlik · Resultatenhet Stugservice (Beskrivning)" />
          </div>
          <div className="card">
            <div className="card-title">Månadsintäkt Stugservice</div>
            {rows.slice(0, 6).map((r, i) => {
              const rev = r[5] || 0;
              return (
                <KpiRow
                  key={i}
                  label={fmtMonth(r[0])}
                  value={rev > 0 ? tkr(rev) : '–'}
                  badge={i === 0 ? { type: 'blue', text: 'Senaste' } : null}
                />
              );
            })}
            <div className="note">Källa: Qlik — Resultatenhet Stugservice</div>
          </div>
          <DagligForsaljning daily={daily} enhet="stugservice" />
        </>
      )}

      {/* ── PERIOD & TREND ── */}
      {sub === 'period' && (
        <>
          <div className="grid-4">
            <Metric
              label="Stugservice YTD"
              value={ytdRev > 0 ? tkr(ytdRev) : '–'}
              sub={ytdTB1 !== 0 ? `TB1 ${tkr(ytdTB1)} · TG ${ytdTG}%` : ''}
              trend={ytdTB1 > 0 ? 'up' : (ytdTB1 < 0 ? 'down' : '')}
            />
            <Metric
              label="Täckningsgrad YTD"
              value={ytdRev > 0 ? `${ytdTG}%` : '–'}
              sub={ytdTB1 !== 0 ? `TB1 ${tkr(ytdTB1)}` : ''}
            />
            <Metric
              label="OMS/H YTD"
              value={ytdOmsH > 0 ? `${ytdOmsH.toLocaleString('sv-SE')} kr/h` : '–'}
              sub={ytdTim > 0 ? `${Math.round(ytdTim)} tim` : ''}
            />
            <Metric
              label="Personal% YTD"
              value={ytdPersPct !== null ? `${ytdPersPct}%` : '–'}
              sub={ytdLon > 0 ? `Lön ${tkr(ytdLon)}` : 'Lön ÷ intäkt'}
              trend={ytdPersPct === null ? '' : ytdPersPct <= 35 ? 'up' : ytdPersPct >= 55 ? 'down' : ''}
            />
          </div>
          <div className="card">
            <div className="card-title">Månadsöversikt Stugservice</div>
            <div className="period-table">
              <div className="period-header period-row-7">
                <span>Månad</span>
                <span>Intäkt</span>
                <span>TB1</span>
                <span>TG%</span>
                <span>Tim</span>
                <span>OMS/H</span>
                <span>Pers%</span>
              </div>
              {rows.map((r, i) => {
                const rev  = r[5]  || 0;
                const tb1  = r[41] || 0;
                const tim  = r[21] || 0;
                const lon  = r[48] || 0;
                // Samma månad ifjol (ÅrMånad − 100) för YoY-coaching
                const fRev = rows.find(x => Number(x[0]) === Number(r[0]) - 100)?.[5] || 0;
                // Procenttal döljs för månader med försumbar intäkt (<50 tkr)
                const pctOk = rev >= 50000;
                const tg   = rev > 0 ? Math.round(tb1 / rev * 100) : 0;
                const omsh = tim > 0 ? Math.round(rev / tim)       : 0;
                const pers = rev > 0 ? Math.round(Math.abs(lon) / rev * 100) : 0;
                return (
                  <div key={i} className="period-row period-row-7">
                    <span>{fmtMonth(r[0])}</span>
                    <span>
                      {rev > 0 ? tkr(rev) : '–'}
                      {fRev > 0 && (
                        <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                          ifjol {tkr(fRev)}{rev > 0 && <span style={{ fontWeight: 600, color: rev >= fRev ? '#3B6D11' : '#A32D2D' }}> · {rev >= fRev ? '+' : ''}{Math.round((rev - fRev) / fRev * 100)}%</span>}
                        </div>
                      )}
                    </span>
                    <span>{tb1 !== 0 ? tkr(tb1) : '–'}</span>
                    <span>{pctOk ? `${tg}%`  : '–'}</span>
                    <span>{tim  > 0 ? Math.round(tim) : '–'}</span>
                    <span>{omsh > 0 ? `${omsh.toLocaleString('sv-SE')} kr` : '–'}</span>
                    <span>{pctOk && lon !== 0 ? `${pers}%` : '–'}</span>
                  </div>
                );
              })}
            </div>
            <div className="note">Källa: Qlik — Resultatenhet Stugservice</div>
          </div>

          {/* YoY-utforskaren — gräv i utvecklingen mot ifjol */}
          <YoYUtforskare enheter={['Stugservice']} hoursEnhet="Stugservice" color={STUG_COLOR} />
        </>
      )}
    </div>
  );
}
